"use client";

import {
  ComponentPropsWithoutRef,
  forwardRef,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";

import { HelpIcon } from "@saas/shared/icon";
import { classNames } from "@saas/shared/utils";

import Tooltip from "../tooltip/tooltip";
import Typography from "../typography/typography";

export interface CheckboxInputProps extends ComponentPropsWithoutRef<"input"> {
  id: string;
  label: string;
  icon?: ReactNode;
  indeterminate?: boolean;
  showLabel?: boolean;
  helpMessage?: string;
  testid?: string;
  className?: string;
  inputClassName?: string;
}

export const CheckboxInput = forwardRef<HTMLInputElement, CheckboxInputProps>(
  (
    {
      indeterminate,
      label,
      icon,
      showLabel = true,
      helpMessage,
      testid = "",
      className,
      inputClassName,
      ...props
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle<HTMLInputElement, HTMLInputElement>(
      ref,
      () => inputRef.current!
    );

    useEffect(() => {
      if (inputRef.current && typeof indeterminate === "boolean") {
        const checked = props.checked || props.defaultChecked;
        inputRef.current.indeterminate = !checked && indeterminate;
      }
    }, [inputRef, indeterminate, props.checked, props.defaultChecked]);

    const checked = props.checked || props.defaultChecked;

    return (
      <div className={"flex items-center"}>
        <input
          {...props}
          type={"checkbox"}
          className={classNames(
            "text-blue-B700 border-neutral-N400 h-5 w-5 cursor-pointer rounded border-2 focus:outline-0 focus:ring-transparent",
            "disabled:text-neutral-N200 disabled:border-neutral-N200 disabled:cursor-not-allowed",
            "checked:disabled:text-blue-B400 checked:disabled:border-blue-B400",
            "indeterminate:disabled:text-blue-B400 indeterminate:disabled:border-blue-B400",
            icon ? "mr-2" : "",
            inputClassName
          )}
          ref={inputRef}
          data-testid={testid}
          aria-checked={!checked && indeterminate ? "mixed" : checked}
        />

        {icon}

        <label htmlFor={props.id}>
          <Typography
            type={"body-b2"}
            className={classNames(
              "text-he truncate",
              props.disabled && "text-disabled cursor-not-allowed",
              showLabel ? "mx-2" : "sr-only",
              className
            )}
            data-testid={testid + "__text"}
          >
            {label}
          </Typography>
        </label>

        {helpMessage ? (
          <Tooltip content={helpMessage} testid={testid}>
            <HelpIcon className={"text-neutral-N600 h-6 w-6"} />
          </Tooltip>
        ) : null}
      </div>
    );
  }
);

CheckboxInput.displayName = "CheckboxInput";

export default CheckboxInput;
